import React, { useState, useEffect, useRef, useMemo } from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import ValidationErrorModalComponent from '../../components/ValidationErrorModalComponent';
import { RecruitmentService } from '../../service/RecruitmentService';
import GeneralUtils from '../../utilities/GeneralUtils';
import { AutoComplete } from 'primereact/autocomplete';
import { InstitutionService } from '../../service/InstitutionService';
import { Dropdown } from 'primereact/dropdown';
import DataTableUtils from '../../utilities/DataTableUtils';
import RecruitmentUtils from '../../utilities/RecruitmentUtils';

const RecruitmentPlanJobEdit = (props) => {
    let emptyJob = {
        id: null,
        recruitmentPlan: { id: null, name: '' },
        type: null,
        institutionJob: { id: null },
        departmentType: '',
        departmentName: '',
        position: '',
        positionClass: '',
        positionLevel: '',
        jobID: '',
        jobList: [],
        vacancyForecasted: false,
        vacancyYear: GeneralUtils.getCurrentYear(),
        vacancySemester: 1
    };

    const [job, setJob] = useState(null);
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isNew, setIsNew] = useState(true);
    const [depTypes, setDepTypes] = useState([])
    const [depNames, setDepNames] = useState([])
    const [positions, setPositions] = useState([])
    const [classes, setClasses] = useState([])
    const [levels, setLevels] = useState([])
    const [jobIds, setJobIds] = useState([])
    const [disableDepName, setDisableDepName] = useState(true);
    const [disableClass, setDisableClass] = useState(true);
    const [disableLevel, setDisableLevel] = useState(true);
    const [disableJobId, setDisableJobId] = useState(true);

    const recruitmentService = useMemo(() => new RecruitmentService(), []);
    const institutionService = useMemo(() => new InstitutionService(), []);

    const toast = useRef(null);

    useEffect(() => {
        let _job = { ...emptyJob, recruitmentPlan: props?.recruitmentPlan, type: props?.type };
        if (props?.value) {
            _job = GeneralUtils.emptyInNullForObjectFields(props?.value);

            if (!props?.recruitmentPlan?.institution?.secret) {
                _job = RecruitmentUtils.institutionJobToRecruitmentPlanJob(_job.institutionJob, _job)
            }

            setIsNew(false);
            setDisableDepName(false);
            setDisableClass(false);
            setDisableLevel(false);
            setDisableJobId(false);
        }

        setJob(_job);

        // console.log("_job: ", _job)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props, recruitmentService]);

    const hideDialog = () => {
        if (typeof props.visibleSetter === 'function') {
            props.visibleSetter(false);
        }
        formik.resetForm();
    };

    const saveDetails = (data) => {
        recruitmentService.saveRecruitmentPlanJob(data).then(
            () => {
                if (typeof props.afterSave === 'function') {
                    props.afterSave(null, isNew);
                }
                hideDialog()
            },
            (error) => {
                // console.log(error)
                if (error?.hasValidationError) {
                    setValidationErrors(error.errorCodes)
                    setShowValidationErrorDialog(true)
                } else {
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
                }
            }
        );
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: job ? job : emptyJob,
        validate: (data) => {
            let errors = {};

            if (props?.type !== RecruitmentUtils.getRecruitmentPlanJobType().INALTI_FUNCTIONARI_PUBLICI) {
                if (!data.departmentType) {
                    errors.departmentType = translatedMessage("form.error.recruitmentPlanJob.departmentType.required");
                }
                if (!data.departmentName) {
                    errors.departmentName = translatedMessage("form.error.recruitmentPlanJob.departmentName.required");
                }
                if (!data.positionClass) {
                    errors.positionClass = translatedMessage("form.error.recruitmentPlanJob.positionClass.required");
                }
                if (!data.positionLevel) {
                    errors.positionLevel = translatedMessage("form.error.recruitmentPlanJob.positionLevel.required");
                }
            }

            if (!data.position) {
                errors.position = translatedMessage("form.error.recruitmentPlanJob.position.required");
            }
            if (!data.jobID || data.jobList.length === 0) {
                errors.jobID = translatedMessage("form.error.recruitmentPlanJob.jobID.required");
            }

            if (data.vacancyForecasted && (!data.vacancyYear || parseInt(data.vacancyYear) === 0)) {
                errors.vacancyYear = translatedMessage("form.error.recruitmentPlanJob.vacancyYear.required");
            }

            if (data.vacancyForecasted && (!data.vacancySemester)) {
                errors.vacancySemester = translatedMessage("form.error.recruitmentPlanJob.vacancySemester.required");
            }

            return errors;
        },
        onSubmit: (data) => {
            saveDetails(data);
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const getDepTypes = async (e = null) => {
        await institutionService.findDepartmentTypeByName({ institutionId: props?.recruitmentPlan?.institution?.id, name: e?.query })
            .then((data) => {
                setDepTypes(data);
                return data;
            })
            .catch((error) => {
                toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 })
            });
    };

    const getDepNames = async (e = null) => {
        if (formik.values.departmentType) {
            await institutionService.findDepartmentNameByName({
                institutionId: props?.recruitmentPlan?.institution?.id, departmentTypeId: formik.values.departmentType.id, name: e?.query
            })
                .then((data) => {
                    setDepNames(data);
                    return data;
                })
                .catch((error) => {
                    toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 })
                });
        }
    };

    const getPositions = async (e = null) => {
        await institutionService.findPositionByName({ institutionId: props?.recruitmentPlan?.institution?.id, name: e?.query })
            .then((data) => {
                setPositions(data);
                return data;
            })
            .catch((error) => {
                toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 })
            });
    };

    const getClasses = async (e = null) => {
        if (formik.values.position) {
            await institutionService.findPositionClassByName({
                institutionId: props?.recruitmentPlan?.institution?.id, positionId: formik.values.position?.id, name: e?.query
            })
                .then((data) => {
                    setClasses(data);
                    return data;
                })
                .catch((error) => {
                    toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 })
                });
        }
    };

    const getLevels = async (e = null) => {
        if (formik.values.position) {
            await institutionService.findPositionLevelByName({
                institutionId: props?.recruitmentPlan?.institution?.id, positionId: formik.values.position?.id, name: e?.query
            })
                .then((data) => {
                    setLevels(data);
                    return data;
                })
                .catch((error) => {
                    toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 })
                });
        }
    };

    const getJobIds = async (e = null) => {
        if (formik.values.position) {
            await institutionService.findInstitutionJobByCriteria({
                institutionId: props?.recruitmentPlan?.institution?.id,
                departmentTypeId: formik.values.departmentType?.id,
                departmentNameId: formik.values.departmentName?.id,
                positionId: formik.values.position?.id,
                positionClassId: formik.values.positionClass?.id,
                positionLevelId: formik.values.positionLevel?.id,
                jobID: e?.query
            })
                .then((data) => {
                    let _jobIds = [];
                    _jobIds = data.items.filter((item) => !formik.values.jobList.find(job => job.jobID === item.jobID))
                    setJobIds(_jobIds);
                    return _jobIds;
                })
                .catch((error) => {
                    toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 })
                });
        }
    };

    const handleSelectChange = (data, resetValues = true) => {
        // console.log("handleSelectChange", data)
        let _targetField = data?.target?.id

        if (_targetField) {
            formik.setFieldValue(_targetField, data.value)
            // setJob( {...job, [_targetField]: data.value} )
            if (data?.target?.id === "departmentType") {
                formik.setFieldValue("departmentName", null)
                formik.setFieldValue("jobID", null)
                setDisableDepName(false)
            }
            if (data?.target?.id === "departmentType") {
                formik.setFieldValue("jobID", null)
            }
            if (data?.target?.id === "position") {
                formik.setFieldValue("positionClass", null)
                formik.setFieldValue("positionLevel", null)
                formik.setFieldValue("jobID", null)
                setDisableClass(false)
                setDisableLevel(false)
                setDisableJobId(false)
            }
            if (data?.target?.id === "positionClass" || data?.target?.id === "positionLevel") {
                formik.setFieldValue("jobID", null)
            }
            if (data?.target?.id === "jobID") {
                formik.setFieldValue("jobList", data.value)
            }
        }
    }

    const dialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-check" className="p-button p-button-primary" form="job-form" type="submit" />
        </>
    );

    return (
        <>
            <Toast ref={toast} />

            <Dialog
                visible={props?.visible}
                header={(job?.id ? translatedMessage('job.edit') : translatedMessage('job.add')) + " - " + translatedMessage('RecruitmentPlanJobType.' + props?.type)}
                modal
                resizable={false}
                className="p-fluid"
                style={{ width: '650px' }}
                footer={dialogFooter}
                onHide={hideDialog}
            >
                <form id="job-form" onSubmit={formik.handleSubmit}>
                    <div className="grid">
                        {!RecruitmentUtils.hideField(props?.type) &&
                            <div className="p-inputgroup col-12 flex-column">
                                <div className='col-12 p-0'>
                                    <span className="p-float-label">
                                        <AutoComplete
                                            dropdown
                                            id="departmentType"
                                            name="departmentType"
                                            value={formik.values.departmentType}
                                            suggestions={depTypes}
                                            completeMethod={(e) => getDepTypes(e)}
                                            virtualScrollerOptions={{ itemSize: 38 }}
                                            field="name"
                                            onChange={handleSelectChange}
                                            emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                            className={classNames({ 'p-invalid': isFormFieldValid('departmentType') })}
                                            autoFocus={true}
                                        />

                                        <label htmlFor="departmentType" className={classNames({ 'p-error': isFormFieldValid('departmentType') })}>
                                            {translatedMessage("job.departmentType")} *
                                        </label>
                                    </span>
                                </div>
                                <div className="p-0 col-12">{getFormErrorMessage('departmentType')}</div>
                            </div>
                        }

                        {!RecruitmentUtils.hideField(props?.type) &&
                            <div className="p-inputgroup col-12 flex-column">
                                <div className='col-12 p-0'>
                                    <span className="p-float-label">
                                        <AutoComplete
                                            dropdown
                                            id="departmentName"
                                            name="departmentName"
                                            value={formik.values.departmentName}
                                            suggestions={depNames}
                                            completeMethod={(e) => getDepNames(e)}
                                            virtualScrollerOptions={{ itemSize: 38 }}
                                            field="name"
                                            onChange={handleSelectChange}
                                            emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                            className={classNames({ 'p-invalid': isFormFieldValid('departmentName') })}
                                            disabled={disableDepName}
                                        />

                                        <label htmlFor="departmentName" className={classNames({ 'p-error': isFormFieldValid('departmentName') })}>
                                            {translatedMessage("job.departmentName")} *
                                        </label>
                                    </span>
                                </div>
                                <div className="p-0 col-12">{getFormErrorMessage('departmentName')}</div>
                            </div>
                        }

                        <div className="p-inputgroup col-12 flex-column">
                            <div className='col-12 p-0'>
                                <span className="p-float-label">
                                    <AutoComplete
                                        dropdown
                                        id="position"
                                        name="position"
                                        value={formik.values.position}
                                        suggestions={positions}
                                        completeMethod={(e) => getPositions(e)}
                                        virtualScrollerOptions={{ itemSize: 38 }}
                                        field="name"
                                        onChange={handleSelectChange}
                                        emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                        className={classNames({ 'p-invalid': isFormFieldValid('position') })}
                                    />

                                    <label htmlFor="position" className={classNames({ 'p-error': isFormFieldValid('position') })}>
                                        {translatedMessage("job.position")} *
                                    </label>
                                </span>
                            </div>
                            <div className="p-0 col-12">{getFormErrorMessage('position')}</div>
                        </div>

                        {!RecruitmentUtils.hideField(props?.type) &&
                            <div className="p-inputgroup col-12 sm:col-6 flex-column">
                                <div className='col-12 p-0'>
                                    <span className="p-float-label">
                                        <AutoComplete
                                            dropdown
                                            id="positionClass"
                                            name="positionClass"
                                            value={formik.values.positionClass}
                                            suggestions={classes}
                                            completeMethod={(e) => getClasses(e)}
                                            virtualScrollerOptions={{ itemSize: 38 }}
                                            field="name"
                                            onChange={handleSelectChange}
                                            emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                            className={classNames({ 'p-invalid': isFormFieldValid('positionClass') })}
                                            disabled={disableClass}
                                        />

                                        <label htmlFor="positionClass" className={classNames({ 'p-error': isFormFieldValid('positionClass') })}>
                                            {translatedMessage("job.positionClass")} *
                                        </label>
                                    </span>
                                </div>
                                <div className="p-0 col-12">{getFormErrorMessage('positionClass')}</div>
                            </div>
                        }

                        {!RecruitmentUtils.hideField(props?.type) &&
                            <div className="p-inputgroup col-12 sm:col-6 flex-column">
                                <div className='col-12 p-0'>
                                    <span className="p-float-label">
                                        <AutoComplete
                                            dropdown
                                            id="positionLevel"
                                            name="positionLevel"
                                            value={formik.values.positionLevel}
                                            suggestions={levels}
                                            completeMethod={(e) => getLevels(e)}
                                            virtualScrollerOptions={{ itemSize: 38 }}
                                            field="name"
                                            onChange={handleSelectChange}
                                            emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                            className={classNames({ 'p-invalid': isFormFieldValid('positionLevel') })}
                                            disabled={disableLevel}
                                        />

                                        <label htmlFor="positionLevel" className={classNames({ 'p-error': isFormFieldValid('positionLevel') })}>
                                            {translatedMessage("job.positionLevel")} *
                                        </label>
                                    </span>
                                </div>
                                <div className="p-0 col-12">{getFormErrorMessage('positionLevel')}</div>
                            </div>
                        }

                        <div className="p-inputgroup col-12 flex-column">
                            <div className='col-12 p-0'>
                                <span className="p-float-label">
                                    <AutoComplete
                                        dropdown
                                        multiple={job?.id ? false : true}
                                        id="jobID"
                                        name="jobID"
                                        value={formik.values.jobID}
                                        suggestions={jobIds}
                                        completeMethod={(e) => getJobIds(e)}
                                        virtualScrollerOptions={{ itemSize: 38 }}
                                        field="jobID"
                                        onChange={handleSelectChange}
                                        emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                        className={classNames({ 'p-invalid': isFormFieldValid('jobID') })}
                                        disabled={disableJobId}
                                    />

                                    <label htmlFor="jobID" className={classNames({ 'p-error': isFormFieldValid('jobID') })}>
                                        {translatedMessage("job.jobID")} *
                                    </label>
                                </span>
                            </div>
                            <div className="p-0 col-12">{getFormErrorMessage('jobID')}</div>
                        </div>

                        <div className="p-inputgroup col-12 sm:col-3">
                            {translatedMessage("job.vacancyForecasted")}
                        </div>
                        <div className="p-inputgroup col-12 sm:col-3 flex-column">
                            <div className='col-12 p-0'>
                                <span className="p-float-label">
                                    <Dropdown
                                        value={formik.values.vacancyForecasted}
                                        options={RecruitmentUtils.yesNoDropdownOptions()}
                                        onChange={(e) => formik.setFieldValue("vacancyForecasted", e.value)}
                                        className="p-column-filter"
                                        itemTemplate={(e) => DataTableUtils.yesNoTemplate(e.value)}
                                    />

                                    <label htmlFor="vacancyForecasted" className={classNames({ 'p-error': isFormFieldValid('vacancyForecasted') })}>
                                        {translatedMessage("generic.yesNo")} *
                                    </label>
                                </span>
                            </div>
                            <div className="p-0 col-12">{getFormErrorMessage('vacancyForecasted')}</div>
                        </div>

                        <div className="p-inputgroup col-12 sm:col-3 flex-column">
                            <div className='col-12 p-0'>
                                <span className="p-float-label">
                                    <InputText type="text" name="vacancyYear" id="vacancyYear"
                                        value={formik.values.vacancyYear} onChange={formik.handleChange}
                                        className={classNames({ 'p-invalid': isFormFieldValid('vacancyYear') })}
                                        keyfilter="int" maxLength={4} disabled={!formik.values.vacancyForecasted} />
                                    <label htmlFor="vacancyYear" className={classNames({ 'p-error': isFormFieldValid('vacancyYear') })}>
                                        {translatedMessage("job.vacancyYear")} *
                                    </label>
                                </span>
                            </div>
                            <div className="p-0 col-12">{getFormErrorMessage('vacancyYear')}</div>
                        </div>

                        <div className="p-inputgroup col-12 sm:col-3 flex-column">
                            <div className='col-12 p-0'>
                                <span className="p-float-label">
                                    <Dropdown
                                        value={formik.values.vacancySemester}
                                        options={RecruitmentUtils.semesterDropdownOptions()}
                                        onChange={(e) => formik.setFieldValue("vacancySemester", e.value)}
                                        className="p-column-filter"
                                        itemTemplate={(e) => translatedMessage("semester." + e.value)}
                                        disabled={!formik.values.vacancyForecasted}
                                    />

                                    <label htmlFor="vacancySemester" className={classNames({ 'p-error': isFormFieldValid('vacancySemester') })}>
                                        {translatedMessage("job.vacancySemester")} *
                                    </label>
                                </span>
                            </div>
                            <div className="p-0 col-12">{getFormErrorMessage('vacancySemester')}</div>
                        </div>

                    </div>
                </form>
            </Dialog>

            {showValidationErrorDialog &&
                <ValidationErrorModalComponent
                    visible={showValidationErrorDialog}
                    errors={validationErrors}
                    dialogTitle={translatedMessage("recruitment.plan.save.error.title")}
                    closeDialog={() => setShowValidationErrorDialog(false)}
                />
            }
        </>
    );
};

export default RecruitmentPlanJobEdit;
